/**
 * CORE-BB-GAP-003 — whether Core's return edges are actually returning.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THIS PANEL IS AN OBSERVER
 * ═══════════════════════════════════════════════════════════════════════════
 * It renders what `core_bb_loop_health(shop_id)` reads from the authorities
 * that already own each number. There is no "Run learning now", no "Fix", no
 * refresh-and-recompute — a reporter that can repair the thing it reports on
 * will eventually report on itself.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THERE IS NO GREEN TICK, AND NO SCORE
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-BB's gap exists because the proficiency edge was severed for the whole
 * life of the product and was found by auditing the tier rather than by
 * looking at it. The capability's own principle is "a severed edge that nobody
 * can see will not be repaired."
 *
 * So this panel reports three things and judges none of them: whether evidence
 * has ever returned, when it last did, and how much there is. It applies NO
 * staleness threshold of its own, because no capability has ruled on what a
 * QUIET shop means and inventing one here would dress a guess as a measurement.
 *
 * Core-100 R2 (HARD-001): what it does show is the server's STALLED verdict for
 * the two edges returned by scheduled sweeps — completed work left waiting for
 * longer than several runs of the sweep's own schedule. That is a stopped
 * mechanism, not a quiet shop. An edge the server does not assess shows nothing
 * here, never "healthy".
 *
 * The state that matters most is NOT BUILT. An edge with no mechanism is not
 * a quiet edge, and showing it as "nothing yet" would tell a manager their
 * shop has not used something nobody can use. Volume is shown as "—" rather
 * than 0 for anything that has not returned: 0 is a measurement, and neither
 * of those states has one.
 */
import { useEffect, useState } from 'react';
import { AlertCircle, CircleDashed, CircleSlash, Loader2, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

export type LoopEdgeState = 'RETURNING' | 'NO_EVIDENCE_YET' | 'NOT_BUILT';

export interface LoopEdge {
  edge_id: string;
  edge_name: string;
  owning_capability: string;
  state: LoopEdgeState;
  last_return_at: string | null;
  evidence_volume: number | null;
  volume_unit: string | null;
  authoritative_source: string | null;
  detail: string | null;
  /** Core-100 R2: the return mechanism's last run, the work waiting for it, and the
   *  server's verdict. NULL = not assessed (an edge with no schedule to measure). */
  mechanism_last_run_at?: string | null;
  pending_inputs?: number | null;
  oldest_pending_at?: string | null;
  stalled?: boolean | null;
}

export function describeStall(edge: LoopEdge): string | null {
  if (edge.stalled !== true) return null;
  const waiting = edge.pending_inputs != null && edge.pending_inputs > 0
    ? `${edge.pending_inputs.toLocaleString()} completed job(s) waiting`
    : 'Completed work is waiting';
  const since = edge.oldest_pending_at ? ` since ${new Date(edge.oldest_pending_at).toLocaleString()}` : '';
  const ran = edge.mechanism_last_run_at
    ? `; last ran ${new Date(edge.mechanism_last_run_at).toLocaleString()}`
    : '; has not run';
  return `Stalled — ${waiting}${since}${ran}.`;
}

/**
 * Core-100 R4 — CORE-BB D: the durable alarm record (`loop_edge_alarms`, raised by `core_bb_raise_loop_alarms`, one
 * open row per stalled edge) had no reader in the product: only the notification it sent was visible. The panel now
 * shows an edge's OPEN alarm — when it was raised and what was waiting — beside the live stall verdict, read under
 * the table's own RLS (shop managers). A failed alarm read is said, never shown as "no alarms".
 */
export interface LoopEdgeAlarm {
  edge_id: string;
  raised_at: string;
  pending_inputs: number | null;
  oldest_pending_at: string | null;
}

export function describeAlarm(a: LoopEdgeAlarm): string {
  const waiting = a.pending_inputs != null && a.pending_inputs > 0
    ? ` with ${a.pending_inputs.toLocaleString()} completed job(s) waiting`
    : '';
  return `Alarm open since ${new Date(a.raised_at).toLocaleString()}${waiting}; managers were notified. It clears on its own when the edge returns evidence again.`;
}

/** Deliberately not a colour scale. Three states, three plain words. */
export function describeState(state: LoopEdgeState): string {
  switch (state) {
    case 'RETURNING': return 'Evidence returning';
    case 'NO_EVIDENCE_YET': return 'No evidence yet';
    case 'NOT_BUILT': return 'Not built';
  }
}

export function describeVolume(edge: LoopEdge): string {
  if (edge.evidence_volume === null) return '—';
  return `${edge.evidence_volume.toLocaleString()}${edge.volume_unit ? ` ${edge.volume_unit}` : ''}`;
}

export function describeLastReturn(edge: LoopEdge): string {
  if (!edge.last_return_at) return edge.state === 'NOT_BUILT' ? '—' : 'never';
  return new Date(edge.last_return_at).toLocaleString();
}

const STATE_ICON: Record<LoopEdgeState, typeof AlertCircle> = {
  RETURNING: RefreshCw,
  NO_EVIDENCE_YET: CircleDashed,
  NOT_BUILT: CircleSlash,
};

interface Props { shopId: string | null | undefined }

export function LoopHealthPanel({ shopId }: Props) {
  const [edges, setEdges] = useState<LoopEdge[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // null = not read (or the read failed — `alarmError` says which); [] = read, none open.
  const [alarms, setAlarms] = useState<LoopEdgeAlarm[] | null>(null);
  const [alarmError, setAlarmError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!shopId) { setLoading(false); setEdges([]); return; }

    (async () => {
      setLoading(true);
      // `src/integrations/supabase/types.ts` is generated and does not yet
      // know this function, so the name is passed through an explicit cast
      // rather than `as never` — which would silence the argument type too.
      // Regenerating the whole types file is a separate, larger change.
      // Called as a member of the client: a detached `supabase.rpc` loses its receiver and throws before any request.
      const client = supabase as unknown as { rpc: (
        fn: string, args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }> };
      const { data, error: err } = await client.rpc('core_bb_loop_health', { p_shop_id: shopId });
      if (cancelled) return;
      // A failed read is not an empty loop. Saying "no edges" here would be
      // the same class of lie the panel exists to prevent.
      if (err) { setError(err.message); setEdges(null); }
      else { setError(null); setEdges((data ?? []) as unknown as LoopEdge[]); }
      // R4: the open alarms, from the durable record (RLS: this shop's managers).
      try {
        const { data: open, error: aErr } = await supabase
          .from('loop_edge_alarms' as never)
          .select('edge_id, raised_at, pending_inputs, oldest_pending_at')
          .eq('shop_id', shopId)
          .is('cleared_at', null);
        if (cancelled) return;
        if (aErr) { setAlarms(null); setAlarmError(true); }
        else { setAlarms((open ?? []) as unknown as LoopEdgeAlarm[]); setAlarmError(false); }
      } catch {
        if (cancelled) return;
        setAlarms(null); setAlarmError(true);
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [shopId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Reading the loop…
      </div>
    );
  }

  if (error) {
    return (
      <Card data-testid="loop-health-error">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            The loop-health record could not be read, so nothing is claimed about it.
          </p>
          <p className="text-xs text-muted-foreground mt-1">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="loop-health-panel">
      <div>
        <h3 className="font-semibold">Learning loop</h3>
        <p className="text-sm text-muted-foreground">
          What completed work has returned to the decisions that use it. Each row
          is read from the record that owns it. The dates and counts are shown as
          they are; an edge is marked stalled only when its scheduled return has
          stopped while completed work waits.
        </p>
      </div>

      {alarmError && (
        <p className="text-xs text-muted-foreground" data-testid="loop-alarms-unread">
          Open stall alarms could not be read just now, so none are claimed either way.
        </p>
      )}

      {(edges ?? []).map((edge) => {
        const Icon = STATE_ICON[edge.state];
        const alarm = (alarms ?? []).find((a) => a.edge_id === edge.edge_id);
        return (
          <Card key={edge.edge_id} data-testid={`loop-edge-${edge.edge_id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {edge.edge_name}
                  </CardTitle>
                  <CardDescription>{edge.owning_capability}</CardDescription>
                </div>
                <Badge
                  variant={edge.state === 'RETURNING' ? 'default' : 'outline'}
                  data-testid={`loop-edge-state-${edge.edge_id}`}
                >
                  {describeState(edge.state)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Last returned</p>
                  <p className="font-medium" data-testid={`loop-edge-last-${edge.edge_id}`}>
                    {describeLastReturn(edge)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Evidence</p>
                  <p className="font-medium" data-testid={`loop-edge-volume-${edge.edge_id}`}>
                    {describeVolume(edge)}
                  </p>
                </div>
              </div>
              {describeStall(edge) && (
                <p role="alert" className="text-sm font-medium text-destructive" data-testid={`loop-edge-stalled-${edge.edge_id}`}>
                  {describeStall(edge)}
                </p>
              )}
              {alarm && (
                <p className="text-sm text-destructive" data-testid={`loop-edge-alarm-${edge.edge_id}`}>
                  {describeAlarm(alarm)}
                </p>
              )}
              {edge.detail && (
                <p className="text-xs text-muted-foreground">{edge.detail}</p>
              )}
              {edge.authoritative_source && (
                <p className="text-xs text-muted-foreground">
                  Source: <code>{edge.authoritative_source}</code>
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
